import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import { appConfigStore } from 'Models/AppConfigStore';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { Checkbox } from 'semantic-ui-react';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import { formatDurationValue } from 'Util/TimeUnit';
import { CardInformation } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/RefundBreakdown';
import { BookingRefundList } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/SelectBookingsContent';
import { TravelCredit } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/TravelCredit';
import If from 'Views/Components/If/If';
import {
	BookingCancellationInformationDto,
	BookingCancellationRefundDto,
	getMethodBookingCutoff,
	getMethodRefundAmount,
	getTravelCreditTerminology,
	handleRefundChange,
	ModalConfig,
	refundMethodChoiceAvailable,
	RefundModel,
} from './NewCancelBookingModal';
import { RefundMethodSelector } from './RefundMethodSelector';

export interface CustomerModalProps {
	cancellationInformation: BookingCancellationInformationDto;
	refundModel: RefundModel;
	extraInfo: JSX.Element;
}

export const buildCustomerModal = (
	cancellationInformation: BookingCancellationInformationDto,
	refundModel: RefundModel,
): ModalConfig => {
	const { invoiced, discounted, isUnpaid, recommendedRefundAmount } = cancellationInformation;

	let extraInfo = <></>;
	const canGiveRefund = recommendedRefundAmount > 0 && !invoiced;
	const refundTerm = getTravelCreditTerminology();

	if (invoiced) {
		extraInfo = <p>This booking is not eligible for a {refundTerm} as it was invoiced.</p>;
	}

	if (discounted && !canGiveRefund) {
		extraInfo = <p>This booking is not eligible for a {refundTerm} as it was fully discounted.</p>;
	}

	if (isUnpaid && !canGiveRefund) {
		extraInfo = <p>This booking is not eligible for a {refundTerm} as it is unpaid.</p>;
	} else if (isUnpaid && canGiveRefund) {
		extraInfo = (
			<p>
				<strong>Note: </strong>Any unpaid amounts are not eligible for a {refundTerm}.
			</p>
		);
	}

	return {
		title: 'Cancel booking',
		content: (
			<CustomerModalContent
				cancellationInformation={cancellationInformation}
				refundModel={refundModel}
				extraInfo={extraInfo}
			/>
		),
		cancelButtonText: 'Back',
		confirmButtonText: 'Confirm',
	};
};

export const getSelectedBookingsCutoffInfo = (
	selectedBookings: BookingCancellationRefundDto[],
	forceCreditRefund: boolean = false,
): { cutoffRules: string[]; totalRecommendedRefund: number } => {
	const cutoffRules: string[] = [];
	let totalRecommendedRefund = 0;

	selectedBookings.forEach(booking => {
		totalRecommendedRefund += getMethodRefundAmount(booking, forceCreditRefund);

		const matchedCutoff = getMethodBookingCutoff(booking, forceCreditRefund);

		if (matchedCutoff) {
			const fee =
				(matchedCutoff.staticFee ?? false)
					? `${formatCurrency(matchedCutoff.feeAmount)}`
					: `${matchedCutoff.feeAmount}%`;

			// An any-time rule has no window — its stored notice period is ignored when matching and is
			// whatever was last saved — so say so rather than naming a window the fee applies outside of.
			if (matchedCutoff.appliesAnytime) {
				cutoffRules.push(`<strong>${fee}</strong> fee when you cancel, <strong>at any time</strong> before departure`);
			} else {
				const timeframe = formatDurationValue(
					matchedCutoff.timeBeforeDepartureValue,
					matchedCutoff.timeBeforeDepartureUnit,
				);
				cutoffRules.push(
					`<strong>${fee}</strong> fee when you cancel within <strong>${timeframe}</strong> of departure`,
				);
			}
		} else {
			cutoffRules.push('No cancellation restrictions');
		}
	});

	return { cutoffRules, totalRecommendedRefund };
};

const getCustomerModalParagraphs = (
	selectedBookings: BookingCancellationRefundDto[],
	messages: { receive: string; notEligible: string },
): { cutoffRules: string[]; refundMessage: string } => {
	const { cutoffRules, totalRecommendedRefund } = getSelectedBookingsCutoffInfo(selectedBookings);

	const refundMessage = totalRecommendedRefund <= 0 ? messages.notEligible : messages.receive;

	return { cutoffRules, refundMessage };
};

export const CustomerModalContent = observer(
	({ cancellationInformation, refundModel, extraInfo }: CustomerModalProps) => {
		const intl = useIntl();

		const { invoiced, discounted, isUnpaid, maximumRefund, recommendedRefundAmount, cardsToRefund, bookings } =
			cancellationInformation;

		const showMethodSelector = refundMethodChoiceAvailable() && !invoiced;

		return (
			<div className="cancel-booking-modal-content">
				<BookingRefundList
					bookings={bookings}
					refundModel={refundModel}
					onRefundChange={(bookingId, amount) => handleRefundChange(bookingId, amount, refundModel)}
				/>

				<If condition={showMethodSelector}>
					<RefundMethodSelector bookings={bookings} refundModel={refundModel} />
				</If>

				{extraInfo && extraInfo}

				{/* Card Refunds */}
				<If
					condition={
						appConfigStore.refundsCustomerAllowedMethods.has('Bank') && !refundModel.forceCreditRefund
					}
				>
					<CardInformation
						cardsToRefund={cardsToRefund}
						refundAmount={refundModel.refundAmount}
						forceFerryCreditRefund={refundModel.forceCreditRefund}
					/>
				</If>

				{/* Ferry Credits (only shown when stripe refunds are not available) */}
				<TravelCredit refundAmount={refundModel.refundAmount} forceFerryCreditRefund={false} />
			</div>
		);
	},
);

const createRefundMessages = (intl: any, recommendedRefundAmount: number) => {
	const formatMessage = (id: string, defaultMessage: string, values?: any) => {
		return intl.formatMessage({ id, defaultMessage }, values);
	};

	if (appConfigStore.refundsCustomerAllowedMethods.has('Credit')) {
		return {
			receive: formatMessage(
				'cancel-booking__refundable-credit__receive',
				`You will receive a ferry voucher with a credited value of ${formatCurrency(recommendedRefundAmount)}.`,
				{ amount: formatCurrency(recommendedRefundAmount) },
			),
			notEligible: formatMessage(
				'cancel-booking__refundable-credit__not-eligible',
				'You will not be eligible for a refund or a voucher.',
			),
		};
	}

	return {
		receive: formatMessage(
			'cancel-booking__non-refundable-credit__receive',
			'Staff will contact you to issue your refund of {amount} in the next few days',
			{ amount: formatCurrency(recommendedRefundAmount) },
		),
		notEligible: formatMessage(
			'cancel-booking__non-refundable-credit__not-eligible',
			'You will not be eligible for a refund.',
			{ amount: formatCurrency(recommendedRefundAmount) },
		),
	};
};
